
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Heart, Receipt, ChevronRight } from "lucide-react";

/**
 * MobileMenuShopping Component
 * 
 * Shopping-related navigation items (cart, wishlist, orders).
 * Phase 2.2: Enhanced with color-coded icons and improved touch targets
 * 
 * Features:
 * - 56px minimum height for accessibility
 * - Color-coded icon backgrounds (emerald for cart, rose for wishlist, violet for orders)
 * - Cart item count badge
 * - Smooth animations and transitions
 * - Wiggle animation on icon hover
 * 
 * @param {MobileMenuShoppingProps} props - Component props
 * @returns {JSX.Element} Shopping navigation items
 */

const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
  },
};

interface MobileMenuShoppingProps {
  itemCount: number;
  handleLinkClick: (path: string) => void;
  handleWishlistClick: () => void;
  handleOrdersClick: () => void;
}

const MobileMenuShopping = ({ itemCount, handleLinkClick, handleWishlistClick, handleOrdersClick }: MobileMenuShoppingProps) => {
  return (
    <>
      <motion.div variants={itemVariants}>
        <motion.button
            onClick={() => handleLinkClick('/cart')}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center justify-between w-full min-h-[48px] px-3 py-2 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
        >
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.3 }}
                className="p-1.5 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors duration-300"
              >
                <ShoppingCart className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </motion.div>
              <span className="font-semibold">Cart</span>
            </div>
            <div className="flex items-center gap-2">
              {itemCount > 0 && (
                <Badge variant="default" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 group-hover:bg-emerald-500/25 font-bold">
                  {itemCount > 99 ? '99+' : itemCount}
                </Badge>
              )}
              <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
            </div>
        </motion.button>
      </motion.div>
      <motion.div variants={itemVariants}>
        <motion.button
            onClick={handleWishlistClick}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center justify-between w-full min-h-[48px] px-3 py-2 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
        >
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.3 }}
                className="p-1.5 rounded-xl bg-rose-500/10 group-hover:bg-rose-500/20 transition-colors duration-300"
              >
                <Heart className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </motion.div>
              <span className="font-semibold">Wishlist</span>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
        </motion.button>
      </motion.div>
      <motion.div variants={itemVariants}>
        <motion.button
            onClick={handleOrdersClick}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center justify-between w-full min-h-[48px] px-3 py-2 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
        >
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.3 }}
                className="p-1.5 rounded-xl bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors duration-300"
              >
                <Receipt className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </motion.div>
              <span className="font-semibold">Orders</span>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
        </motion.button>
      </motion.div>
    </>
  );
};

export default MobileMenuShopping;
